import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  IPlatformToolComponent,
  IPlatformToolHandler
} from "@shared/components/platform-tool/platform-tool-container/platform-tool-component";
import { InvalidateSubject } from "@shared/helpers/invalidate.subject";
import { MagicLinkModel, TabModel } from "@shared/models/platform-tool-models";
import { PlatformCommandResponse } from "@shared/commands/command-base";
import { PlatformQuery } from "@shared/models/platform-query/platform-query";
import { PlatformQueryService } from "@core/services/platform-query.service";
import { Api } from "@shared/constants/common-field.constants";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

type ChangeStatus = 'Added' | 'Modified' | 'Deleted';

// One renderable line inside a value cell.
//   subKey === '' -> the whole value is primitive; just render value.
//   subKey != ''  -> render "<subKey>: <value>" so complex objects appear as
//                    a small list of nested key/value pairs.
interface PreviewEntry {
  subKey: string;
  value: string;
}

interface MetadataChange {
  key: string;
  status: ChangeStatus;
  oldValue: any;
  newValue: any;
  oldEntries: PreviewEntry[];
  newEntries: PreviewEntry[];
}

interface SchemaChangeGroup {
  schemaId: string;
  title: string;            // e.g. "US-Annual-GMT v1.0"
  subtitle: string;         // e.g. countryCode · formType · toolName · version
  changes: MetadataChange[];
  addedCount: number;
  modifiedCount: number;
  deletedCount: number;
  trackBy: (i: number, c: MetadataChange) => string;  // bound per group for the table
}

interface FormMetadataKeyValue {
  key: string;
  value: any;
}

interface FormMetadataChangeModel {
  id?: string;
  schemaId?: string;
  metaData?: FormMetadataKeyValue[];
}

@Component({
  selector: 'app-form-metadata-import-preview',
  standalone: false,
  templateUrl: './form-metadata-import-preview.component.html',
  styleUrl: './form-metadata-import-preview.component.scss'
})
export class FormMetadataImportPreviewComponent implements OnInit, OnDestroy, IPlatformToolComponent {
  handler: IPlatformToolHandler;
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  selectedTab: TabModel;

  isLoading = false;
  errorMessage: string | null = null;
  schemaGroups: SchemaChangeGroup[] = [];
  totalChanges = 0;
  displayedColumns: string[] = ['status', 'key', 'oldValue', 'newValue'];

  private unSubscribe$ = new Subject<void>();

  constructor(private platformQueryService: PlatformQueryService) {
  }

  ngOnInit(): void {
    this.fetchAndDiff();
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  onAfterExecuteNextCommand(_: PlatformCommandResponse) {
  }

  onAfterExecutePreviousCommand(_: PlatformCommandResponse) {
  }

  onBeforeExecuteNextCommand(): boolean {
    return false;
  }

  onBeforeExecutePreviousCommand(): boolean {
    return false;
  }

  onLoad() {
  }

  trackBySchemaId = (_: number, group: SchemaChangeGroup) => group.schemaId;

  // Pulls every schema's live FormConfigModel + its FormMetadataChangeModel in
  // one call (the list branch of FormConfigModelByIdQueryHandler), then diffs
  // each change model against its matching config to build per-schema sections.
  private fetchAndDiff(): void {
    const query = new PlatformQuery();
    query.name = 'FormConfigModelByIdQuery';
    query.api = Api.FilingManagerApi;
    query.fieldValues['IncludeMetaDataChangeModel'] = true;
    // GetFormSchemaModels filters by IsLive — default true so we preview against
    // the currently-published config.
    query.fieldValues['IsLive'] = true;

    this.isLoading = true;
    this.errorMessage = null;
    this.platformQueryService.executeQuery(query)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          const configs: any[] = response?.fieldValues?.['FormConfigModels'] ?? [];
          const changeModels: FormMetadataChangeModel[] = response?.fieldValues?.['FormMetaDataChangeModels'] ?? [];
          this.schemaGroups = this.buildSchemaGroups(configs, changeModels);
          this.totalChanges = this.schemaGroups.reduce((sum, g) => sum + g.changes.length, 0);
        },
        error: (err) => {
          this.isLoading = false;
          this.errorMessage = err?.message ?? 'Failed to load metadata preview.';
        }
      });
  }

  // Index configs by id so each change model can find its matching live config.
  // Emit one SchemaChangeGroup per change model that has at least one real diff
  // — schemas with zero changes are skipped so the UI stays focused.
  private buildSchemaGroups(configs: any[], changeModels: FormMetadataChangeModel[]): SchemaChangeGroup[] {
    const configById = new Map<string, any>();
    for (const cfg of configs) {
      const id = cfg?.id ?? cfg?.Id;
      if (id) configById.set(id, cfg);
    }

    const groups: SchemaChangeGroup[] = [];
    for (const cm of changeModels) {
      const schemaId = cm?.schemaId ?? cm?.id;
      if (!schemaId) continue;

      const config = configById.get(schemaId);
      const changes = this.computeChanges(config, cm);
      if (changes.length === 0) continue;

      const added = changes.filter(c => c.status === 'Added').length;
      const modified = changes.filter(c => c.status === 'Modified').length;
      const deleted = changes.filter(c => c.status === 'Deleted').length;

      groups.push({
        schemaId,
        title: this.buildTitle(config),
        subtitle: this.buildSubtitle(config, schemaId),
        changes,
        addedCount: added,
        modifiedCount: modified,
        deletedCount: deleted,
        trackBy: (_: number, c: MetadataChange) => c.key
      });
    }

    // Most-changed groups first; ties broken alphabetically by title.
    // groups.sort((a, b) => {
    //   const lenDiff = b.changes.length - a.changes.length;
    //   return lenDiff !== 0 ? lenDiff : a.title.localeCompare(b.title);
    // });
    groups.sort((a, b) => a.title.localeCompare(b.title));
    return groups;
  }

  private buildTitle(config: any): string {
    const country = config?.countryCode ?? config?.CountryCode ?? '';
    const formType = config?.formType ?? config?.FormType ?? '';
    const tool = config?.toolName ?? config?.ToolName ?? '';
    const version = config?.version ?? config?.Version ?? '';
    const head = [country, formType, tool].filter(Boolean).join('-');
    return version ? `${head} Version : ${version}` : (head || '(unknown schema)');
  }

  private buildSubtitle(config: any, fallbackId: string): string {
    const id = config?.id ?? config?.Id ?? fallbackId;
    return `Id: ${id}`;
  }

  // Diff rules (mirror the import/update consumers' semantics).
  // A null value and a missing key are both treated as "no real value" — so
  // (old=null, new=null) and (old=missing, new=null) are no-ops, not deletes.
  //
  //   oldReal && !newReal           -> Deleted
  //   !oldReal && newReal           -> Added
  //   oldReal && newReal && differs -> Modified
  //   everything else               -> skipped (unchanged or no-op)
  private computeChanges(existing: any, changeModel: FormMetadataChangeModel | null | undefined): MetadataChange[] {
    const existingFieldValues = existing?.fieldValues ?? existing?.FieldValues ?? {};
    const entries = changeModel?.metaData ?? [];

    const changes: MetadataChange[] = [];
    for (const entry of entries) {
      if (!entry?.key) continue;

      const hasOldKey = Object.prototype.hasOwnProperty.call(existingFieldValues, entry.key);
      const oldValue = hasOldKey ? existingFieldValues[entry.key] : undefined;
      const newValue = entry.value;

      const oldReal = hasOldKey && oldValue !== null && oldValue !== undefined;
      const newReal = newValue !== null && newValue !== undefined;

      let status: ChangeStatus | null = null;
      if (oldReal && !newReal) status = 'Deleted';
      else if (!oldReal && newReal) status = 'Added';
      else if (oldReal && newReal && !this.deepEqual(oldValue, newValue)) status = 'Modified';

      if (status == null) continue;

      changes.push({
        key: entry.key,
        status,
        oldValue,
        newValue,
        oldEntries: this.buildPreviewEntries(oldValue),
        newEntries: this.buildPreviewEntries(newValue)
      });
    }

    // Sort within a schema: Deleted, Modified, Added — then by key alphabetically.
    const order: Record<ChangeStatus, number> = { Deleted: 0, Modified: 1, Added: 2 };
    changes.sort((a, b) => {
      const so = order[a.status] - order[b.status];
      return so !== 0 ? so : a.key.localeCompare(b.key);
    });
    return changes;
  }

  // Cheap deep-equal via JSON serialization. Sufficient here because values come
  // from the same backend serializer on both sides (FieldValues + MetaData).
  private deepEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a !== 'object' && typeof b !== 'object') return String(a) === String(b);
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }

  // Renders a value as a list of PreviewEntry lines inside the cell.
  //   primitive       -> [{ subKey: '', value: '<text>' }]
  //   nested object   -> [{ subKey: 'Street', value: '123 Main' }, ...]
  //   array           -> [{ subKey: '[0]', value: 'Tax' }, ...]
  //   nested mix      -> [{ subKey: 'Lines[0].City', value: 'NY' }, ...]
  // Empty list signals "no value" — the template renders a placeholder.
  private buildPreviewEntries(value: any): PreviewEntry[] {
    if (value === null || value === undefined) return [];
    if (typeof value !== 'object') {
      return [{ subKey: '', value: this.formatLeaf(value) }];
    }
    const out: PreviewEntry[] = [];
    this.flattenInto(out, '', value);
    if (out.length === 0) {
      // Empty {} or [] — still surface the shape rather than disappearing.
      out.push({ subKey: '', value: Array.isArray(value) ? '[ ]' : '{ }' });
    }
    return out;
  }

  // Same dot/bracket grammar the C# FlattenMetadata exporter uses:
  //   object members -> "Parent.Child"
  //   array elements -> "Parent[0]"
  // SubKey is relative to the top-level field (which is already shown in the
  // "Field" column), so we avoid repeating it inside the cell.
  private flattenInto(out: PreviewEntry[], prefix: string, value: any): void {
    if (value === null || value === undefined) {
      out.push({ subKey: prefix, value: this.formatLeaf(value) });
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        out.push({ subKey: prefix, value: '[ ]' });
        return;
      }
      for (let i = 0; i < value.length; i++) {
        this.flattenInto(out, `${prefix}[${i}]`, value[i]);
      }
      return;
    }
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        out.push({ subKey: prefix, value: '{ }' });
        return;
      }
      for (const k of keys) {
        const childKey = prefix === '' ? k : `${prefix}.${k}`;
        this.flattenInto(out, childKey, value[k]);
      }
      return;
    }
    out.push({ subKey: prefix, value: this.formatLeaf(value) });
  }

  private formatLeaf(value: any): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  }
}
